import { useEffect, useState } from 'react';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Modal from '../components/Modal';
import type { User, Department } from '../lib/types';
import { api } from '../lib/api';

interface SettingsProps {
  user: User;
}

const Icons = {
  company: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" />
      <path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" />
    </svg>
  ),
  ai: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.27A7 7 0 0 1 7.27 19H6a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
      <circle cx="10" cy="13" r="1" /><circle cx="14" cy="13" r="1" />
    </svg>
  ),
  dept: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  trash: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
};

export default function Settings({ user }: SettingsProps) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    api.departments.list().then(res => {
      setDepartments(res.departments as unknown as Department[]);
    }).catch(() => {});
    api.employees.list().then(res => {
      setEmployeeCount((res.employees as unknown[]).length);
    }).catch(() => {});
  }, []);

  const handleAddDept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    try {
      await api.departments.create({ name: newDeptName.trim() });
      setNewDeptName('');
      setShowDeptModal(false);
      const res = await api.departments.list();
      setDepartments(res.departments as unknown as Department[]);
    } catch {
      // handle silently
    }
  };

  const handleDeleteDept = async (id: string) => {
    try {
      await api.departments.delete(id);
      setDeleteConfirm(null);
      const res = await api.departments.list();
      setDepartments(res.departments as unknown as Department[]);
    } catch {
      // handle silently
    }
  };

  const planLabel = user.plan || 'free';
  const planLimit = planLabel === 'business' ? 50 : planLabel === 'enterprise' ? 500 : 10;
  const usagePercent = Math.min((employeeCount / planLimit) * 100, 100);

  const planBadge = () => {
    switch (planLabel) {
      case 'business': return <Badge bg="rgba(74,124,255,0.1)" color="#4A7CFF">ビジネス</Badge>;
      case 'enterprise': return <Badge bg="rgba(74,222,128,0.1)" color="#4ADE80">エンタープライズ</Badge>;
      default: return <Badge bg="rgba(139,146,168,0.1)" color="#8B92A8">フリー</Badge>;
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-text-main mb-1">設定</h2>
      <p className="text-[13px] text-text-sub mb-6">企業情報・プラン・部署の管理</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Company Info */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-text-muted">{Icons.company}</span>
            <span className="text-[13px] font-semibold text-text-main">企業情報</span>
          </div>
          <div className="space-y-0">
            {([
              ['企業名', user.company_name || '-'],
              ['管理者メール', user.email],
              ['ロール', user.role === 'admin' ? '管理者' : user.role === 'manager' ? 'マネージャー' : 'メンバー'],
            ] as [string, string][]).map(([k, v], i) => (
              <div key={i} className="flex justify-between py-2.5 border-b border-border text-[13px]">
                <span className="text-text-sub">{k}</span>
                <span className="text-text-main font-medium">{v}</span>
              </div>
            ))}
            <div className="flex justify-between items-center py-2.5 text-[13px]">
              <span className="text-text-sub">プラン</span>
              {planBadge()}
            </div>
          </div>

          {/* Usage Bar */}
          <div className="mt-4 p-3 bg-surface rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-text-sub">社員登録数</span>
              <span className="text-xs text-text-main font-semibold">{employeeCount} / {planLimit}名</span>
            </div>
            <div className="w-full h-2 rounded-full bg-bg overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${usagePercent}%`,
                  background: usagePercent > 90 ? '#FF6B6B' : usagePercent > 70 ? '#FBBF24' : '#4A7CFF',
                }}
              />
            </div>
          </div>
        </Card>

        {/* AI Usage */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-text-muted">{Icons.ai}</span>
            <span className="text-[13px] font-semibold text-text-main">AI利用状況</span>
            <Badge bg="rgba(139,146,168,0.1)" color="#8B92A8">今月</Badge>
          </div>
          <div className="space-y-0">
            {([
              ['API呼び出し回数', '---'],
              ['トークン使用量', '---'],
              ['推定コスト', '---'],
              ['キャッシュヒット率', '---'],
            ] as [string, string][]).map(([k, v], i) => (
              <div key={i} className="flex justify-between py-2.5 border-b border-border text-[13px]">
                <span className="text-text-sub">{k}</span>
                <span className="text-text-muted font-medium">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 p-2.5 bg-surface rounded-lg border border-dashed border-border">
            <div className="text-[11px] text-text-muted text-center">
              AI利用統計の詳細表示はPhase 2で実装予定です
            </div>
          </div>
        </Card>
      </div>

      {/* Department Management */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="text-text-muted">{Icons.dept}</span>
            <span className="text-[13px] font-semibold text-text-main">部署管理</span>
            <span className="text-[11px] text-text-muted">（{departments.length}部署）</span>
          </div>
          <Button small primary onClick={() => setShowDeptModal(true)}>+ 部署を追加</Button>
        </div>
        {departments.length === 0 ? (
          <div className="text-sm text-text-muted py-6 text-center">
            部署が登録されていません。「+ 部署を追加」から登録してください。
          </div>
        ) : (
          <div className="space-y-1.5">
            {departments.map(dept => (
              <div key={dept.id} className="flex justify-between items-center py-2.5 px-3 bg-surface rounded-lg border border-border hover:border-border-active transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-[13px] text-text-main font-medium">{dept.name}</span>
                  {dept.employee_count !== undefined && dept.employee_count > 0 && (
                    <Badge bg="rgba(74,124,255,0.1)" color="#4A7CFF">{dept.employee_count}名</Badge>
                  )}
                </div>
                {deleteConfirm === dept.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-accent">削除しますか？</span>
                    <button
                      onClick={() => handleDeleteDept(dept.id)}
                      className="text-[11px] text-accent hover:text-white bg-accent/20 hover:bg-accent px-2 py-0.5 rounded transition-colors"
                    >
                      はい
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="text-[11px] text-text-muted hover:text-text-main px-2 py-0.5 rounded transition-colors"
                    >
                      いいえ
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(dept.id)}
                    className="text-text-muted hover:text-accent transition-colors p-1"
                  >
                    {Icons.trash}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Trademark Notice */}
      <div className="mt-8 text-[10px] text-text-muted leading-relaxed text-center">
        TEKIZAI v1.5 - Phase 1<br />
        MBTIはMyers & Briggs Foundation, Inc.の登録商標です。<br />
        本サービスは公式MBTI製品ではありません。ユング心理学に基づく16タイプ分類を使用しています。
      </div>

      {/* Add Department Modal */}
      <Modal open={showDeptModal} onClose={() => setShowDeptModal(false)} title="部署を追加">
        <form onSubmit={handleAddDept} className="space-y-3">
          <div>
            <label className="block text-xs text-text-sub mb-1">部署名 *</label>
            <input
              required
              value={newDeptName}
              onChange={e => setNewDeptName(e.target.value)}
              placeholder="例：営業部"
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setShowDeptModal(false)}>キャンセル</Button>
            <Button primary type="submit">追加</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
